import React, { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { Alert, Button, Card, Col, Row, Slider, Table, Tooltip as AntdTooltip } from 'antd';
import * as BizCharts from 'bizcharts';
import { DownloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import {
	MAX_SUPPLIERS_IN_TABLE,
	PERFORMER_GROUP_ORDER,
	AXIS_Y_LINE_COLOR,
	CHART_PRIMARY_COLOR,
} from './LifeCycleAnalyticsConstants';

const { Chart, Axis, Geom, Legend, Tooltip } = BizCharts as any;

const WO_PERFORMANCE_TABLE_SCROLL_Y = 390;

const WoPerformanceSection = ({
	chartColor,
	filteredSupplierPerformance,
	supplierTableColumns,
	selectedPriorityCard,
	minWoCount,
	setMinWoCount,
	formatDurationAxisLabel,
	formatPerformerShape,
	formatPerformerSize,
	formatPerformerStyle,
	buildPerformanceTooltip,
	handleWoPerformancePointClick,
	exportSupplierPerformanceCsv,
}) => {
	const effectiveChartColor = chartColor;
	const chartSupplierPerformance = filteredSupplierPerformance || [];
	const [tableScrollYDisabledForPrint, setTableScrollYDisabledForPrint] = useState(false);

	useEffect(() => {
		if (typeof window === 'undefined') {
			return undefined;
		}
		const onBeforePrint = () => {
			flushSync(() => {
				setTableScrollYDisabledForPrint(true);
			});
		};
		const onAfterPrint = () => {
			setTableScrollYDisabledForPrint(false);
		};
		window.addEventListener('beforeprint', onBeforePrint);
		window.addEventListener('afterprint', onAfterPrint);
		const mq = window.matchMedia ? window.matchMedia('print') : null;
		const onPrintMq = () => {
			if (mq && mq.matches) {
				flushSync(() => {
					setTableScrollYDisabledForPrint(true);
				});
			} else {
				setTableScrollYDisabledForPrint(false);
			}
		};
		if (mq && mq.addEventListener) {
			mq.addEventListener('change', onPrintMq);
		}
		return () => {
			window.removeEventListener('beforeprint', onBeforePrint);
			window.removeEventListener('afterprint', onAfterPrint);
			if (mq && mq.removeEventListener) {
				mq.removeEventListener('change', onPrintMq);
			}
		};
	}, []);

	return (
		<Card
			className="print:!h-auto print:!min-h-0"
			title={
				<div className="flex min-w-0 flex-nowrap items-center gap-3">
					{selectedPriorityCard && (
						<AntdTooltip title={selectedPriorityCard.woPriorityName}>
							<span
								className="inline-block min-w-0 max-w-[250px] shrink-0 truncate rounded-2xl px-3 py-1 align-middle text-sm font-medium leading-[22px]"
								style={{
									color: selectedPriorityCard.color,
									backgroundColor: selectedPriorityCard.bgColor,
								}}
							>
								{selectedPriorityCard.woPriorityName}
							</span>
						</AntdTooltip>
					)}
					<span className="h5medium text-character-title/85">WO Performance</span>
					<div className="box-border inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-[2px] border-0 border-b border-solid border-grey-5 bg-grey-2 px-3 py-1 print:!hidden">
						<AntdTooltip title="Filter suppliers by minimum number of completed work orders">
							<InfoCircleOutlined className="text-sm text-character-secondary" />
						</AntdTooltip>
						<span className="footnoteregular mr-1 shrink-0 text-gray-500">
							Minimum WOs completed by supplier:
						</span>
						<div className="flex shrink-0 items-center">
							<Slider
								min={0}
								max={1000}
								step={10}
								value={minWoCount}
								onChange={setMinWoCount}
								className="woPerformanceMinWoSlider !m-0 w-[120px] shrink-0"
								trackStyle={{ backgroundColor: effectiveChartColor }}
								handleStyle={{ borderColor: effectiveChartColor }}
							/>
						</div>
						<span className="min-w-8 shrink-0 whitespace-nowrap text-right text-xs font-normal leading-none text-grey-8">
							{minWoCount}
						</span>
					</div>
				</div>
			}
			extra={
				<div className="flex items-center gap-2">
					<AntdTooltip title="Download">
						<Button
							className="print:!hidden"
							icon={<DownloadOutlined />}
							onClick={exportSupplierPerformanceCsv}
						/>
					</AntdTooltip>
				</div>
			}
		>
			<Row
				gutter={0}
				className="woPerformanceChartRow print:!m-0 print:!flex print:!w-full print:!max-w-full print:!flex-col print:!flex-nowrap print:!items-center"
			>
				<Col
					span={12}
					className="border-0 border-r border-solid border-grey-5 print:!order-1 print:!w-full print:!max-w-[920px] print:!flex-none print:!basis-auto print:!self-center print:!border-r-0 print:!px-3 print:!pb-4"
				>
					{filteredSupplierPerformance.length > MAX_SUPPLIERS_IN_TABLE && (
						<Alert
							className="!mb-0 !rounded-none !border-none bg-character-dangerBg"
							type="error"
							banner
							showIcon={false}
							closable={false}
							message={`Too much data to display. showing ${MAX_SUPPLIERS_IN_TABLE}/${filteredSupplierPerformance.length} suppliers. Filter to narrow selection`}
						/>
					)}
					<div className="w-full min-w-0 print:!flex print:!break-inside-avoid print:!justify-center">
						<Chart
							height={430}
							appendPadding={[57, 36, 40, 16]}
							data={chartSupplierPerformance.slice(0, MAX_SUPPLIERS_IN_TABLE)}
							autoFit={true}
							forceFit={true}
							theme="openwrench"
							scale={{
								avgResponseTimeMinutes: {
									nice: true,
								},
								avgResolutionMinutes: {
									nice: true,
								},
								performerGroup: {
									values: PERFORMER_GROUP_ORDER,
								},
							}}
							onPointClick={handleWoPerformancePointClick}
						>
							<Axis
								name="avgResponseTimeMinutes"
								title={{
									text: 'Avg. Response Time',
									style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
									offset: 40,
								}}
								label={{
									formatter: formatDurationAxisLabel,
									style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
								}}
								line={{ style: { stroke: AXIS_Y_LINE_COLOR } }}
								tickLine={null}
								grid={{
									line: {
										style: {
											stroke: AXIS_Y_LINE_COLOR,
											lineDash: [4, 4],
										},
									},
								}}
							/>
							<Axis
								name="avgResolutionMinutes"
								title={{
									text: 'Avg. Resolution Time',
									style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
									offset: 70,
								}}
								label={{
									formatter: formatDurationAxisLabel,
									style: { fontSize: 12, fill: 'rgba(0,0,0,0.45)' },
								}}
								line={{ style: { stroke: AXIS_Y_LINE_COLOR } }}
								tickLine={null}
								grid={{
									line: {
										style: {
											stroke: AXIS_Y_LINE_COLOR,
											lineDash: [4, 4],
										},
									},
								}}
							/>
							<Legend position="bottom" />
							<Tooltip
								showTitle={false}
								crosshairs={false}
								itemTpl={
									'<li data-index={index} style="list-style:none;padding:0;margin:0;">{value}</li>'
								}
								domStyles={{
									'g2-tooltip': {
										padding: '0',
										boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
										borderRadius: '4px',
									},
									'g2-tooltip-list': {
										margin: '0',
										padding: '0',
									},
								}}
							/>
							<Geom
								type="point"
								position="avgResponseTimeMinutes*avgResolutionMinutes"
								color={effectiveChartColor}
								shape={['performerGroup*isInternalTeam', formatPerformerShape]}
								size={['performerGroup*isInternalTeam', formatPerformerSize]}
								tooltip={[
									'supplierFacilityName*woCount*avgResponseTimeSeconds*avgResolutionTimeSeconds',
									buildPerformanceTooltip,
								]}
								style={['performerGroup*isInternalTeam', formatPerformerStyle]}
							/>
						</Chart>
					</div>
				</Col>
				<Col
					span={12}
					className="print:!order-2 print:!w-full print:!max-w-[920px] print:!flex-none print:!basis-auto print:!self-center print:!border-b-0 print:!border-l-0 print:!border-r-0 print:!border-t print:!border-solid print:!border-grey-5 print:!px-3 print:!pt-4"
				>
					<Table
						className="w-full"
						columns={supplierTableColumns}
						rowKey={(row) => `${row.supplierFacilityId}`}
						pagination={false}
						size="small"
						scroll={tableScrollYDisabledForPrint ? undefined : { y: WO_PERFORMANCE_TABLE_SCROLL_Y }}
						dataSource={filteredSupplierPerformance.slice(0, MAX_SUPPLIERS_IN_TABLE)}
					/>
				</Col>
			</Row>
		</Card>
	);
};

export default WoPerformanceSection;
